import React, { useMemo } from 'react'
import classNames from 'classnames'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBan } from '@fortawesome/pro-solid-svg-icons/faBan'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Tooltip } from 'antd'
import { CourseStateType, CourseType, EnrolState } from 'enum'
import ImageFull, { ImageSizes } from 'components/ui/image-full/ImageFull'
import { mobileWidth } from 'enum/responsive-width.enum'
import { useWindowSize } from 'hooks/react.hooks'
import { sanitizeHtml } from 'helpers/sanitizeHtml'
import { ICourseCard } from 'models/courseCard.model'
import { useAccess } from 'contexts/AccessContext'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import {
  Tags,
  ProgressBarStateCourse,
  RestartCourse,
  RequiresRequest,
  CourseCardBase,
} from '../../atoms'
import { reduceAbstract } from '../../../utils'
import {
  useCourseStatus,
  useAmplitudeActions,
  useStartCourse,
} from '../../../hooks'
import { ICardEnrol } from '../../../types'
import { ExternalCourseIndicator } from '../externalCourseIndicator'
import styles from './CourseVerticalCard.module.scss'
import { COURSE_VERTICAL_CARD_WIDTH } from './constants'
import { DraftCourse, DraftCourseTooltip, InactiveCardSection } from './styled'

interface ICourseVerticalCardProps {
  course: ICourseCard
  directToLessons?: boolean
  className?: string
  enrol: ICardEnrol | null
  enrolled: boolean
  swiping?: () => boolean
  width?: string | number
  style?: React.CSSProperties
}

export const CourseVerticalCard: React.FC<ICourseVerticalCardProps> = ({
  course,
  directToLessons,
  className,
  enrol,
  enrolled,
  swiping,
  width = COURSE_VERTICAL_CARD_WIDTH,
  style,
}) => {
  const { t } = useTranslation()
  const { isAdmin } = useAccess()

  const description = useMemo(
    () => reduceAbstract(course.description),
    [course]
  )

  const requiresRequest = course.courseType === CourseType.Closed

  const courseStatus = useCourseStatus()

  const amplitudeActions = useAmplitudeActions({
    course,
    enrolled,
    enrol,
  })

  const startCourse = useStartCourse({
    course,
    directToLessons,
    enrol,
  })

  const handleButton = () => {
    if (swiping && swiping()) return
    startCourse()
    amplitudeActions()
  }

  const windowsSize = useWindowSize()

  const onDesktop =
    windowsSize.width && windowsSize.width < mobileWidth ? false : true

  const percent = enrol?.progressPercentage || 0

  const isPending = enrol?.stateAdmin === EnrolState.Pending
  const isApproved = enrol?.stateAdmin === EnrolState.Approved

  const showState =
    !!enrolled &&
    (requiresRequest ? isApproved : true) &&
    course.state !== CourseStateType.Soon

  const state = !!showState && enrol && (
    <span className={styles.courseStatus}>
      {courseStatus(enrol.advance)}
      <div className="flex-fill text-right">{percent}%</div>
    </span>
  )

  const hasFullAccess = isAdmin()
  const isInactive = course.state === CourseStateType.Inactive
  const isDraft = course.state === CourseStateType.Draft
  const fadedCard = isDraft || isInactive

  return (
    <CourseCardBase
      id={`${course.id}`}
      className={classNames(className, styles.card)}
      onClick={() => (isDraft ? undefined : handleButton())}
      style={{
        width,
        ...style,
      }}
    >
      <InactiveCardSection
        className={`${styles.courseImage}`}
        $inactive={fadedCard}
      >
        <Tags course={course} enrol={enrol} />
        <ImageFull
          className={`image`}
          src={course.thumbnailUrl || course.bannerUrl}
          sizes={ImageSizes.Small}
        />
      </InactiveCardSection>
      <InactiveCardSection className={styles.cardBody} $inactive={fadedCard}>
        <ExternalCourseIndicator
          origin="VERTICAL_CARD"
          url={course['integrationCourseLink']}
        />
        <div className={styles.text}>
          <div className={styles.titleContent}>
            {course.name.length >= 35 && onDesktop ? (
              <CommonToolTip text={course.name} position="bottom">
                <h1 className={`${styles.courseTitle} ${styles.truncate} `}>
                  {course.name}
                </h1>
              </CommonToolTip>
            ) : (
              <h1 className={`${styles.courseTitle} ${styles.truncate} `}>
                {course.name}
              </h1>
            )}
          </div>
          <div
            className={[
              styles.regularText14,
              styles.courseDescription,
              styles.truncate,
            ].join(' ')}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(description),
            }}
          />
        </div>
        {course.state !== 0 && (
          <div className={styles.courseButton}>
            <div className={styles.bottomContainer}>
              {!!showState && !!enrol && (
                <div>
                  <ProgressBarStateCourse
                    progress={percent}
                    advance={enrol.advance}
                  />
                </div>
              )}
              <Row>
                <Col xs={12}>
                  {showState ? (
                    <div
                      className={[styles.smallText12, styles.courseState].join(
                        ' '
                      )}
                    >
                      {state}
                    </div>
                  ) : (
                    !hasFullAccess &&
                    requiresRequest && <RequiresRequest isPending={isPending} />
                  )}
                </Col>
                {!!(course.canRestart && enrol) && (
                  <Col xs={12}>
                    <RestartCourse
                      advance={enrol.advance}
                      approved={enrol.approved}
                      courseId={course.id}
                    />
                  </Col>
                )}
              </Row>
            </div>
          </div>
        )}
      </InactiveCardSection>
      {course.state === 0 && (
        <Tooltip
          title={
            <DraftCourseTooltip>
              {t('components.courseCard.draftCourse.tooltip')}
            </DraftCourseTooltip>
          }
        >
          <DraftCourse>
            <FontAwesomeIcon icon={faBan as IconProp} />
            {t('components.courseCard.draftCourse.title')}
          </DraftCourse>
        </Tooltip>
      )}
    </CourseCardBase>
  )
}

export default CourseVerticalCard
